import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import { select } from '@ps/web-core-store';
import { StarsLink } from '@ps/web-shared-ui';
import {
  required,
  validateCPR,
  validateDNI,
  validateNIE,
  validateNIF
} from '@ps/web-shared-validators';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';

import { checkSelfExclusionDK } from '../../../../../../../../validators/checkSelfExclusionDK';
import {
  TaxCodeType,
  checkTaxCode,
  taxLabel,
  uniqueValidationMessage
} from '../../../../../../../../validators/taxCode';
import TextInput from '../../../../../../../TextInput/TextInput';

import {
  TXTCLI_Your_must_enter_a_valid_DNIX,
  TXTWCL_Contact_Xa_hrefXXmailtoXverifi_ELL_1,
  TXTWCL_For_more_informationX_you_can__ELL,
  TXTWCL_It_looks_like_you_have_elected_ELL,
  TXTWCL_NIF_format_incorrect,
  TXTWCL_Please_add_your_DNIX_NIE_or_ta_ELL,
  TXTWCL_Please_check_for_mistake_or_ad_ELL,
  TXTWCL_Please_check_that_your_CPR_num_ELL,
  TXTWCL_Please_enter_your_NIF,
  TXTWCL_Please_enter_your_identificati_ELL_1,
  TXTWCL_Please_enter_your_information__ELL,
  TXTWCL_Please_log_in_to_an_existing_X_ELL,
  TXTWCL_Please_log_in_to_your_existing_ELL,
  TXTWCL_Wrong_CPR_number_format,
  TXTWCL_You_may_only_own_a_single_Star_ELL,
  TXTWCL_You_may_only_use_a_NIF_assigne_ELL,
  TXTWCL_You_will_not_be_able_to_open_a_ELL,
  TXTWCL_Your_must_enter_a_valid_NIE
} from './TaxCodeField.messages.json';

const parse = (value: string) => value.replace(/[^A-Za-z0-9]/g, '');

interface Props {
  taxCodeType: TaxCodeType;
  labelContent?: React.ReactNode;
  className?: string;
  dataTestId?: string;
  country?: string;
  citizenship?: string;
}

const maxLengthValidator = {
  [TaxCodeType.NIF]: 9,
  [TaxCodeType.DNI]: 20,
  [TaxCodeType.NONE]: 20,
  [TaxCodeType.NIE]: 20,
  [TaxCodeType.CPR]: 10
};

const DOCUMENT_NUMBER_SPAIN_MAX_LENGTH = 33;
const DOCUMENT_NUMBER_GREECE_MAX_LENGTH = 30;

const getMaxLength = (
  taxCodeType: TaxCodeType,
  siteId: Sites,
  isNonSpanishCitizenOrResident: boolean
) => {
  if (isNonSpanishCitizenOrResident) {
    return DOCUMENT_NUMBER_SPAIN_MAX_LENGTH;
  }
  if (siteId === Sites.GR) {
    return DOCUMENT_NUMBER_GREECE_MAX_LENGTH;
  }
  return maxLengthValidator[taxCodeType];
};

const TaxCodeField: React.FC<Props> = ({
  taxCodeType,
  labelContent,
  className,
  country,
  citizenship
}) => {
  const options = useSelector(select.cms.getRequestOptions);
  const starsDomain = useSelector(select.cms.getStarsDomain);
  const siteId = useSelector(select.cms.getSiteId);
  const dob = useSelector(select.account.getDateOfBirth);
  const isSpain = siteId === Sites.SPA;

  const isNonSpanishCitizenOrResident =
    isSpain && country !== Country.Spain && citizenship !== Country.Spain;

  const openWindowContactSupport = useOpenWindow({
    resolverTag: ResolverTag.ContactSupport,
    centered: true
  });

  const taxCodeValidators = useMemo(() => {
    const validateTaxCode = {
      [TaxCodeType.CPR]: [
        required(TXTWCL_Please_enter_your_information__ELL),
        validateCPR(dob, TXTWCL_Please_check_that_your_CPR_num_ELL, TXTWCL_Wrong_CPR_number_format),
        checkSelfExclusionDK(
          options,
          TXTWCL_It_looks_like_you_have_elected_ELL,
          TXTWCL_You_will_not_be_able_to_open_a_ELL,
          TXTWCL_For_more_informationX_you_can__ELL
        )
      ],
      [TaxCodeType.DNI]: [required(), validateDNI(TXTCLI_Your_must_enter_a_valid_DNIX)],
      [TaxCodeType.NIE]: [required(), validateNIE(TXTWCL_Your_must_enter_a_valid_NIE)],
      [TaxCodeType.NIF]: [
        required(TXTWCL_Please_enter_your_NIF),
        validateNIF(TXTWCL_NIF_format_incorrect, [
          TXTWCL_You_may_only_use_a_NIF_assigne_ELL,
          TXTWCL_Please_check_for_mistake_or_ad_ELL,
          {
            ...TXTWCL_Contact_Xa_hrefXXmailtoXverifi_ELL_1,
            values: {
              a: () => (
                <StarsLink
                  href={`mailto:verification@${starsDomain}`}
                >{`verification@${starsDomain}`}</StarsLink>
              )
            }
          }
        ])
      ],
      [TaxCodeType.NONE]: [required()]
    };

    return [
      ...validateTaxCode[taxCodeType],
      checkTaxCode(
        options,
        [
          uniqueValidationMessage[taxCodeType],
          TXTWCL_You_may_only_own_a_single_Star_ELL,
          taxCodeType === TaxCodeType.NIF
            ? {
                ...TXTWCL_Please_log_in_to_your_existing_ELL,
                values: {
                  a: () => (
                    <StarsLink
                      href={`mailto:verification@${starsDomain}`}
                    >{`verification@${starsDomain}`}</StarsLink>
                  )
                }
              }
            : {
                ...TXTWCL_Please_log_in_to_an_existing_X_ELL,
                values: {
                  a: (msg: React.ReactNode) => (
                    <StarsLink onClick={openWindowContactSupport}>{msg}</StarsLink>
                  )
                }
              }
        ],
        taxCodeType
      )
    ];
  }, [openWindowContactSupport, options, taxCodeType, starsDomain, dob]);

  const validator = useMemo(() => {
    switch (taxCodeType) {
      case TaxCodeType.NONE:
        return [
          required(TXTWCL_Please_enter_your_identificati_ELL_1),
          ...(isSpain
            ? [checkTaxCode(options, TXTWCL_Please_add_your_DNIX_NIE_or_ta_ELL, taxCodeType)]
            : [])
        ];
      default:
        return taxCodeValidators;
    }
  }, [taxCodeType, taxCodeValidators, options, isSpain]);

  return (
    <TextInput
      id="nationalDocumentNumber"
      maxLength={getMaxLength(taxCodeType, siteId, isNonSpanishCitizenOrResident)}
      className={className}
      parse={parse}
      label={taxLabel[taxCodeType]}
      labelContent={labelContent}
      validators={validator}
      checkDisabled
      data-testid={taxCodeType}
    />
  );
};

export default TaxCodeField;
